import * as Sentry from '@sentry/nextjs';
import { useCallback } from 'react';

import { isProduction } from '@/helpers/common';
import { getCustomerIOId } from '@/helpers/customerio';
import { createWaitForGlobal } from '@/helpers/waitForGlobal';
import { useGetCommonStore } from '@/store';

enum CustomerIOMode {
  ON = 0,
  OFF = 1,
}

type CustomerIOMap = {
  [key in CustomerIOMode]: {
    identifyUser: (id: number) => void;
    resetUser: () => void;
    handleEvents: () => void;
    trackEvent: (eventName: string, data: Record<string, unknown>) => void;
  };
};

const getCio = () => {
  const cio = (window as any)._cio;
  return cio && typeof cio.identify === 'function' ? cio : null;
};

const waitForCio = createWaitForGlobal(getCio, { name: 'CustomerIO' });

/**
 * IN - APP - for users who was logged in
 */
export const customerIOMap: CustomerIOMap = {
  [CustomerIOMode.ON]: {
    identifyUser: (id: number) => {
      waitForCio()
        .then((cio) => {
          try {
            cio.identify({ id: getCustomerIOId(id) });
          } catch (err) {
            Sentry.addBreadcrumb({
              message: 'CustomerIO identifyUser failed',
              category: 'customerIO',
              level: 'error',
            });
            console.error('CustomerIO identifyUser failed', err);
          }
        })
        .catch(() => {});
    },
    resetUser: () => {
      const cio = (window as any)._cio;
      if (!cio || typeof cio.reset !== 'function') return;
      try {
        cio.reset();
      } catch {
        Sentry.addBreadcrumb({
          message: 'CustomerIO resetUser failed',
          category: 'customerIO',
          level: 'error',
        });
        console.error('CustomerIO resetUser failed');
      }
    },
    handleEvents: () => {
      waitForCio()
        .then((cio) => {
          try {
            cio.on('in-app:message-action', (event: any) => {
              if (event.detail.action === 'open_intercom') {
                window.Intercom('show');
                event.detail.message.dismiss();
              }
            });
          } catch {
            console.error('CustomerIO handleEvents failed');
          }
        })
        .catch(() => {});
    },
    trackEvent: (eventName: string, data: Record<string, unknown>) => {
      waitForCio()
        .then((cio) => {
          try {
            cio.track(eventName, data);
          } catch {
            Sentry.addBreadcrumb({
              message: `CustomerIO trackEvent failed for event: ${eventName}`,
              category: 'customerIO',
              level: 'error',
            });
            console.error(
              `CustomerIO trackEvent failed for event: ${eventName}`
            );
          }
        })
        .catch(() => {});
    },
  },
  [CustomerIOMode.OFF]: {
    identifyUser: () => {},
    resetUser: () => {},
    handleEvents: () => {},
    trackEvent: (eventName: string) => {
      console.log('DEV MODE: customer.io event should be tracking', eventName);
    },
  },
};

export const isLinkFromCustomerIOEmail = (searchParams: URLSearchParams) =>
  (searchParams.get('utm_source') || '').toLowerCase() === 'customer.io' &&
  (searchParams.get('utm_medium') || '').toLowerCase() === 'email_action';

const CustomerIO = isProduction()
  ? customerIOMap[CustomerIOMode.ON]
  : customerIOMap[CustomerIOMode.OFF];

export const useCustomerIO = () => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();

  const initCustomerIO = useCallback(() => {
    if (!user?.id) return;
    CustomerIO.identifyUser(user.id);
    CustomerIO.handleEvents();
  }, [user]);

  const resetCustomerIO = useCallback(() => {
    CustomerIO.resetUser();
  }, []);

  const trackCustomEvent = useCallback(
    (eventName: string) => {
      if (!user?.id) return;
      CustomerIO.trackEvent(eventName, { user_id: user.id });
    },
    [user]
  );
  return { initCustomerIO, resetCustomerIO, trackCustomEvent };
};
